import React from 'react';
import { motion } from 'framer-motion';
import { aiSuite, microAnalysisSuite } from '../data/features';
import { Reveal, Stagger, staggerItem, CountUp } from './MotionPrimitives';

const microHighlights = [
  'Who’s in the app now',
  'Save left carts',
  'Star vs slow products',
  'Busy shopping hours',
  'Free Cash still unused',
  'Counter busy hours',
  'Compare branches',
  'What staff should do next',
];

export default function AISuite() {
  return (
    <section id="ai" className="section-pad bg-ink">
      <div className="mx-auto max-w-7xl">
        <Reveal className="mx-auto max-w-3xl text-center">
          <span className="pill-dark">Smart Helpers</span>
          <h2 className="mt-5 text-3xl font-extrabold tracking-tight text-white sm:text-4xl md:text-5xl">
            Intelligence That <span className="grad-text">Speaks Retail</span>
          </h2>
          <p className="mt-4 text-mute">
            Four AI helpers for stock, buying, new shops, and ads. Plus{' '}
            <span className="text-white">Micro Analysis</span> to see who is shopping and which sales
            you can still save.
          </p>
        </Reveal>

        <div className="mt-10 flex flex-wrap justify-center gap-10">
          {[
            { n: 4, label: 'AI helpers', color: 'text-yellow-400' },
            { n: 8, label: 'Micro Analysis views', color: 'text-sky-400' },
            { n: 2, suffix: 'm', label: 'live shopper window', color: 'text-emerald-400' },
          ].map((s) => (
            <div key={s.label} className="text-center">
              <p className={`text-4xl font-extrabold ${s.color}`}>
                <CountUp end={s.n} />
                {s.suffix || ''}
              </p>
              <p className="mt-1 text-sm text-faint">{s.label}</p>
            </div>
          ))}
        </div>

        <Reveal className="mt-14">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
            Control-panel AI
          </p>
          <h3 className="mt-2 text-2xl font-bold text-white sm:text-3xl">
            Four AI helpers built for retailers
          </h3>
        </Reveal>

        <Stagger className="mt-8 grid gap-5 md:grid-cols-2">
          {aiSuite.map((mod, i) => (
            <motion.article key={mod.id} variants={staggerItem} className="il-card">
              <div className="flex items-center justify-between">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent/15 text-sm font-bold text-accent">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="text-xs text-faint">{mod.role}</span>
              </div>
              <h3 className="mt-4 text-xl font-bold text-white">{mod.name}</h3>
              <p className="mt-2 text-sm leading-relaxed text-mute">{mod.description}</p>
              <ul className="mt-5 space-y-2">
                {mod.notes.map((n) => (
                  <li key={n} className="flex gap-2 text-sm text-mute">
                    <span className="text-accent">•</span>
                    {n}
                  </li>
                ))}
              </ul>
            </motion.article>
          ))}
        </Stagger>

        <Reveal className="mt-20">
          <article className="relative overflow-hidden rounded-3xl border border-cyan-400/35 bg-gradient-to-br from-sky-500/20 via-violet-500/10 to-card p-6 shadow-glow sm:p-8 md:p-10">
            <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-cyan-400/25 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-violet-500/20 blur-3xl" />

            <div className="relative">
              <div className="flex flex-wrap items-center gap-3">
                <span className="rounded-full bg-grad-btn px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white">
                  Customers → Micro Analysis
                </span>
                <span className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-[11px] font-semibold text-emerald-300">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
                  Live
                </span>
              </div>

              <h3 className="mt-4 text-2xl font-extrabold text-white sm:text-3xl md:text-4xl">
                Micro Analysis - {' '}
                <span className="bg-gradient-to-r from-sky-300 to-cyan-300 bg-clip-text text-transparent">
                  Save sales before they slip
                </span>
              </h3>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-mute sm:text-base">
                One place to see who’s shopping, which carts were left, which products are slow, and
                what your team should do next.
              </p>

              <div className="mt-6 flex flex-wrap gap-2">
                {microHighlights.map((h) => (
                  <span
                    key={h}
                    className="rounded-lg border border-cyan-400/25 bg-cyan-500/10 px-2.5 py-1.5 text-[11px] text-cyan-50/90 sm:text-xs"
                  >
                    {h}
                  </span>
                ))}
              </div>

              <div className="mt-10 grid gap-4 sm:grid-cols-2">
                {microAnalysisSuite.map((desk, i) => (
                  <motion.div
                    key={desk.id}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-40px' }}
                    transition={{ delay: 0.04 * i }}
                    className="rounded-2xl border border-white/10 bg-black/45 p-5 backdrop-blur-sm"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-cyan-400/20 text-xs font-bold text-cyan-300">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <span className="text-[11px] text-faint">{desk.role}</span>
                    </div>
                    <h4 className="mt-3 text-lg font-bold text-white">{desk.name}</h4>
                    <p className="mt-2 text-sm leading-relaxed text-mute">{desk.description}</p>
                    <ul className="mt-4 space-y-1.5">
                      {desk.notes.map((n) => (
                        <li key={n} className="flex gap-2 text-xs text-white/70 sm:text-[13px]">
                          <span className="text-cyan-300">•</span>
                          {n}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>

              <div className="mt-8 rounded-2xl border border-white/10 bg-black/40 p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-300">
                  Made for shop owners
                </p>
                <p className="mt-2 text-sm leading-relaxed text-mute">
                  Short tips only - call this customer, push this offer, restock this item, remind
                  before Free Cash expires. No heavy reports needed.
                </p>
              </div>
            </div>
          </article>
        </Reveal>
      </div>
    </section>
  );
}
